import { useHistory } from 'react-router-dom';
import { Avatar, Popover, TopBar } from '@panviva/panviva-react-ui';
import logo from '../assets/images/logo.png';
import { useState, useEffect, useRef, useMemo } from 'react';
import { RootState } from '../app/store';
import { useSelector } from 'react-redux';
import "./Header.scss";
import { LoadingIndicator } from './common/LoadingIndicator';
import pulseTitleLogo from "./../assets/images/pulse-title-logo.svg";
import { forceLogoutToLogin } from '../utils/auth/forceLogout';
import {
    clearPendingTenantChangeNotice,
    getPendingTenantChangeNotice,
    getTenantOptionsFromUser,
    initializeActiveTenantForSession,
    parseTenantChangePayload,
    publishTenantChange,
    setActiveTenantCode,
    setPendingTenantChangeNotice,
    TENANT_CHANGE_BROADCAST_STORAGE_KEY,
    TenantChangePayload,
} from '../utils/tenantSession';

const PAGE_TITLE_OVERRIDES: { prefix: string; title: string }[] = [
    { prefix: '/sidekick/chat-history', title: 'Sidekick' },
    { prefix: '/engage/analytics', title: 'Engage' },
];

const getPageTitle = (pathname: string): string => {
    const override = PAGE_TITLE_OVERRIDES.find(entry => pathname.startsWith(entry.prefix));
    return override ? override.title : 'Panviva Pulse';
};

const hasPageTitleOverride = (pathname: string): boolean =>
    PAGE_TITLE_OVERRIDES.some(entry => pathname.startsWith(entry.prefix));

const Header = ({ menuOpen, setMenuOpen, ...props }: any) => {

    const history = useHistory();
    const location = history.location;
    // Public/post-auth routes show only branding — no avatar, no popover, no
    // user-data effects. Skips Redux selectors and side effects that depend on
    // an authenticated user.
    const isPublicRoute = location.pathname === '/logout';
    const [userInfo, setUserInfo] = useState({ firstName: '', lastName: '', fullName: '', email: '' });
    const [activeTenantCode, setActiveTenantCodeState] = useState('');
    const [tenantError, setTenantError] = useState<string | null>(null);
    const [retryTenantCode, setRetryTenantCode] = useState<string | null>(null);
    const [tenantChangeNotice, setTenantChangeNotice] = useState<TenantChangePayload | null>(null);
    const [isSiteDetailsHovered, setIsSiteDetailsHovered] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);
    const firstItemRef = useRef<HTMLLIElement>(null);
    const latestTenantChangeAtRef = useRef(0);
    const user = useSelector<RootState, any>(
        (state) => state?.users?.user || null
    );
    const loading = useSelector<RootState, boolean>(
        (state) => state?.users?.loading
    );
    const tenantOptions = useMemo(() => getTenantOptionsFromUser(user), [user]);

    useEffect(() => {
        if (isPublicRoute) {
            return;
        }
        if (user && Object.keys(user).length > 0) {
            localStorage.setItem('userDetailsEUM', JSON.stringify(user));
            const tenantSetup = initializeActiveTenantForSession(user);
            setActiveTenantCodeState(tenantSetup.activeTenantCode);
            setTenantError(tenantSetup.error);
            setRetryTenantCode(null);

            const pendingTenantChange = getPendingTenantChangeNotice();
            if (pendingTenantChange) {
                latestTenantChangeAtRef.current = pendingTenantChange.changedAt;

                if (pendingTenantChange.tenantCode === tenantSetup.activeTenantCode) {
                    clearPendingTenantChangeNotice();
                    setTenantChangeNotice(null);
                } else {
                    setTenantChangeNotice(pendingTenantChange);
                }
            }

            const userNameSplit = user?.fullName?.split(' ') || [];
            const firstName = userNameSplit[0] || '';
            const lastName = userNameSplit[userNameSplit.length - 1] || '';
            setUserInfo({
                firstName: firstName || '',
                lastName: lastName || '',
                fullName: user.fullName || '',
                email: user?.emailAddress || ''
            });
        }
    }, [user, isPublicRoute]);

    useEffect(() => {
        if (isPublicRoute) {
            return;
        }

        const onTenantChangeBroadcast = (event: StorageEvent) => {
            if (event.key !== TENANT_CHANGE_BROADCAST_STORAGE_KEY || !event.newValue) {
                return;
            }

            const payload = parseTenantChangePayload(event.newValue);
            if (!payload) {
                return;
            }

            if (payload.changedAt <= latestTenantChangeAtRef.current) {
                return;
            }

            latestTenantChangeAtRef.current = payload.changedAt;

            if (payload.tenantCode === activeTenantCode) {
                clearPendingTenantChangeNotice();
                setTenantChangeNotice(null);
                return;
            }

            setPendingTenantChangeNotice(payload);
            setTenantChangeNotice(payload);
        };

        window.addEventListener('storage', onTenantChangeBroadcast);

        return () => {
            window.removeEventListener('storage', onTenantChangeBroadcast);
        };
    }, [activeTenantCode, isPublicRoute]);

    const handleTenantSwitch = (tenantCode: string) => {
        if (!tenantCode || tenantCode === activeTenantCode) {
            return;
        }

        if (!tenantOptions.some((tenant) => tenant.code === tenantCode)) {
            setTenantError('Selected tenant is not authorized for this user.');
            setRetryTenantCode(null);
            return;
        }

        const didPersist = setActiveTenantCode(tenantCode);
        if (!didPersist) {
            setTenantError('Could not switch tenant. Please retry.');
            setRetryTenantCode(tenantCode);
            return;
        }

        setTenantError(null);
        setRetryTenantCode(null);
        setActiveTenantCodeState(tenantCode);

        const tenantToBroadcast = tenantOptions.find((tenant) => tenant.code === tenantCode);
        if (tenantToBroadcast) {
            publishTenantChange(tenantToBroadcast);
        }

        window.location.reload();
    };

    const handleTenantChangeRefresh = () => {
        clearPendingTenantChangeNotice();
        const isSidekickDetailRoute = /^\/sidekick\/chat-history\/[^/]+/.test(location.pathname);

        if (isSidekickDetailRoute) {
            window.location.assign('/sidekick/chat-history');
            return;
        }

        window.location.reload();
    };

    const handleDismissTenantChangeNotice = () => {
        setTenantChangeNotice(null);
    };

    const handleClickProfile = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setIsSiteDetailsHovered(false);
        setAnchorEl(null);
    };

    const handleSiteDetailsMouseEnter = () => {
        if (tenantOptions.length <= 0 || !!tenantError) {
            return;
        }
        setIsSiteDetailsHovered(true);
    };

    const handleSiteDetailsMouseLeave = () => {
        setIsSiteDetailsHovered(false);
    };

    const handleSiteDetailsBlurCapture = (event: React.FocusEvent<HTMLElement>) => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
            return;
        }

        setIsSiteDetailsHovered(false);
    };

    const handleProfileKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setAnchorEl(e.currentTarget);
        } else if (e.key === 'Tab' && open && !e.shiftKey) {
            // When popover is open and user tabs forward, move focus to logout button
            e.preventDefault();
            firstItemRef.current?.focus();
        }
    };

    const handleLogoutKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            forceLogoutToLogin();
        } else if (e.key === 'Tab' && !e.shiftKey) {
            // Close popover when tabbing forward from logout
            e.preventDefault();
            handleClose();
        } else if (e.key === 'Tab' && e.shiftKey) {
            // Close popover when shift-tabbing backward from logout
            e.preventDefault();
            handleClose();
        }
    };

    const handlePopoverKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            handleClose();
        }
    };

    if (isPublicRoute) {
        return (
            <div className="topbar-wrapper">
                <TopBar
                    style={{ height: 56 }}
                    navSize={56}
                    label="Panviva Pulse"
                    logo={logo}
                />
                <img src={pulseTitleLogo} alt="Panviva Pulse Logo" className='pulse-title-logo' />
            </div>
        );
    }

    return (
        <>
            <LoadingIndicator busy={loading} />
            <div className="topbar-wrapper">
                <TopBar
                    style={{ height: 56 }}
                    navSize={56}
                    label={getPageTitle(location.pathname)}
                    barButtons={
                        <button
                            data-testid='profile-icon'
                            className="profile-icon-wrapper"
                            onClick={handleClickProfile}
                            onKeyDown={handleProfileKeyDown}
                            type="button"
                            tabIndex={1}
                            aria-label={`User profile menu${userInfo.fullName ? `, ${userInfo.fullName}` : ''}`}
                            aria-expanded={open}
                            aria-haspopup="true">
                            <Avatar
                                firstName={userInfo.firstName}
                                lastName={userInfo.lastName}
                                className="avatar"
                            />
                        </button>}
                    logo={logo}
                    {...props}
                />
                {!hasPageTitleOverride(location.pathname) && (
                    <img src={pulseTitleLogo} alt="Panviva Pulse Logo" className='pulse-title-logo' />
                )}
            </div>
            <Popover
                className="pv-popover"
                open={open}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                onClose={() => handleClose()}
                aria-label="User profile menu"
            >
                <ul className="pv-popover-list" onKeyDown={handlePopoverKeyDown}>
                    {userInfo.fullName && (
                        <li className="pv-popover-item user-name" aria-label={`User name: ${userInfo.fullName}`}>
                            {userInfo.fullName}
                        </li>
                    )}
                    {userInfo.email && (
                        <li className="pv-popover-item user-email" aria-label={`Email: ${userInfo.email}`}>
                            {userInfo.email}
                        </li>
                    )}
                    {!tenantError && tenantOptions.length > 0 ? (
                        <li
                            className="pv-popover-item site-details-container"
                            onMouseEnter={handleSiteDetailsMouseEnter}
                            onMouseLeave={handleSiteDetailsMouseLeave}
                            onFocusCapture={handleSiteDetailsMouseEnter}
                            onBlurCapture={handleSiteDetailsBlurCapture}
                        >
                            <div
                                data-testid="site-details-trigger"
                                className={`site-details-trigger${isSiteDetailsHovered ? ' open' : ''}`}
                                role="button"
                                tabIndex={0}
                                aria-label="Site details"
                                aria-expanded={isSiteDetailsHovered}
                            >
                                <span>Site Details</span>
                                <span className="site-details-chevron" aria-hidden="true">
                                    {isSiteDetailsHovered ? '▾' : '▸'}
                                </span>
                            </div>
                            {isSiteDetailsHovered && (
                                <ul className="tenant-flyout-list" aria-label="Authorized tenants">
                                    {tenantOptions.map((tenant) => {
                                        const isActiveTenant = tenant.code === activeTenantCode;

                                        return (
                                            <li
                                                key={tenant.code}
                                                className={`pv-popover-item tenant-option${isActiveTenant ? ' active' : ''}`}
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => handleTenantSwitch(tenant.code)}
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter' || event.key === ' ') {
                                                        event.preventDefault();
                                                        handleTenantSwitch(tenant.code);
                                                    }
                                                }}
                                                aria-label={`${tenant.name}${isActiveTenant ? ', active site' : ''}`}
                                            >
                                                <span>{tenant.name}</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </li>
                    ) : (
                        <li className={`pv-popover-item ${tenantError ? 'tenant-error' : 'tenant-empty'}`} aria-live="polite">
                            <span>{tenantError || 'No tenant details available.'}</span>
                        </li>
                    )}
                    <li
                        ref={firstItemRef}
                        className="pv-popover-item logout-button"
                        role="button"
                        tabIndex={0}
                        onClick={forceLogoutToLogin}
                        onKeyDown={handleLogoutKeyDown}
                        aria-label="Logout"
                    >
                        Logout
                    </li>
                </ul>
            </Popover>
            {tenantChangeNotice && (
                <div className="tenant-change-toast" role="alert" aria-live="polite" aria-atomic="true">
                    <span className="tenant-change-toast-message">
                        The active tenant has changed to <span className="tenant-change-toast-tenant-name">{tenantChangeNotice.tenantName}</span>.
                        <br />
                        Refresh this page to view the latest data.
                    </span>
                    <div className="tenant-change-toast-actions">
                        <button
                            type="button"
                            className="tenant-change-toast-refresh"
                            onClick={handleTenantChangeRefresh}
                        >
                            Refresh
                        </button>
                        <button
                            type="button"
                            className="tenant-change-toast-close"
                            onClick={handleDismissTenantChangeNotice}
                            aria-label="Close tenant change notification"
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default Header;
